import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App as CapApp } from '@capacitor/app';
import { Keyboard } from '@capacitor/keyboard';
import { Network } from '@capacitor/network';
import { SplashScreen } from '@capacitor/splash-screen';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import { WifiSlash, ScanSmiley } from '@phosphor-icons/react';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { isNative } from '../lib/platform';

// Lock again after the app has been in the background this long.
const RELOCK_AFTER_MS = 5 * 60 * 1000;

// App-only behavior: deep links, Android back button, offline screen and the
// Face ID / fingerprint lock. Renders nothing extra in a browser.
export default function NativeShell({ children }) {
  if (!isNative) return children;
  return <NativeShellInner>{children}</NativeShellInner>;
}

function NativeShellInner({ children }) {
  const navigate = useNavigate();
  const { session, loading } = useAuth();
  const [online, setOnline] = useState(true);
  const [locked, setLocked] = useState(false);
  const [biometryAvailable, setBiometryAvailable] = useState(null); // null = still checking
  const [coldStartChecked, setColdStartChecked] = useState(false);
  const [unlockError, setUnlockError] = useState('');
  const [inBackground, setInBackground] = useState(false);
  const backgroundedAt = useRef(null);

  // Hide iOS's previous / next / done bar above the keyboard (iOS only; no-op elsewhere)
  useEffect(() => {
    Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(() => {});
  }, []);

  // Splash stays up until we know whether the user is signed in and whether to lock
  useEffect(() => {
    if (coldStartChecked) SplashScreen.hide().catch(() => {});
  }, [coldStartChecked]);

  // Links from emails (clinicianscore.com/...) open the matching screen in the app
  useEffect(() => {
    const sub = CapApp.addListener('appUrlOpen', ({ url }) => {
      try {
        const u = new URL(url);
        const path = u.protocol.startsWith('http') ? u.pathname : `/${u.host}${u.pathname}`;
        navigate(`${path.replace(/\/+$/, '') || '/'}${u.search}${u.hash}`);
      } catch {
        // ignore malformed links
      }
    });
    return () => { sub.then((h) => h.remove()); };
  }, [navigate]);

  // Android hardware back button
  useEffect(() => {
    // The web view's own canGoBack reports false after in-app navigation, so use
    // React Router's history index instead (0 = the first screen of this session).
    const sub = CapApp.addListener('backButton', () => {
      if ((window.history.state?.idx ?? 0) > 0) window.history.back();
      else CapApp.exitApp();
    });
    return () => { sub.then((h) => h.remove()); };
  }, []);

  // Connection status
  useEffect(() => {
    Network.getStatus().then((s) => setOnline(s.connected)).catch(() => {});
    const sub = Network.addListener('networkStatusChange', (s) => setOnline(s.connected));
    return () => { sub.then((h) => h.remove()); };
  }, []);

  // Biometric lock: on cold start with a saved session, and on return after RELOCK_AFTER_MS
  useEffect(() => {
    NativeBiometric.isAvailable({ useFallback: false })
      .then((r) => setBiometryAvailable(!!r.isAvailable))
      .catch(() => setBiometryAvailable(false));
  }, []);

  // Wait for both the saved session and the biometric check before deciding,
  // so a slow check can't let the app open unlocked
  useEffect(() => {
    if (coldStartChecked || loading || biometryAvailable === null) return;
    if (session && biometryAvailable) setLocked(true);
    setColdStartChecked(true);
  }, [coldStartChecked, loading, session, biometryAvailable]);

  useEffect(() => {
    const sub = CapApp.addListener('appStateChange', ({ isActive }) => {
      setInBackground(!isActive);
      if (!isActive) {
        backgroundedAt.current = Date.now();
        return;
      }
      const away = backgroundedAt.current ? Date.now() - backgroundedAt.current : 0;
      backgroundedAt.current = null;
      if (away >= RELOCK_AFTER_MS && session && biometryAvailable) setLocked(true);
    });
    return () => { sub.then((h) => h.remove()); };
  }, [session, biometryAvailable]);

  // Signed out (or session expired) means nothing to protect
  useEffect(() => {
    if (!session) setLocked(false);
  }, [session]);

  const unlock = async () => {
    setUnlockError('');
    try {
      await NativeBiometric.verifyIdentity({
        reason: 'Unlock CScore',
        title: 'Unlock CScore',
        negativeButtonText: 'Cancel',
        maxAttempts: 3,
      });
      setLocked(false);
    } catch {
      setUnlockError("Couldn't verify it's you. Try again or sign in with your password.");
    }
  };

  // Prompt right away when the lock screen appears
  useEffect(() => {
    if (locked) unlock();
  }, [locked]);

  // Keep clinic data off screen (and away from screen readers) while locked
  const hideContent = locked || !coldStartChecked;

  return (
    <>
      <div aria-hidden={hideContent} style={hideContent ? { visibility: 'hidden' } : undefined}>
        {children}
      </div>
      {locked && (
        <FullScreen>
          <ScanSmiley size={56} weight="light" className="text-white mb-6" />
          <h1 className="text-white text-2xl font-semibold mb-2">CScore is locked</h1>
          <p className="text-white/80 text-sm mb-8 max-w-[280px]">
            Unlock with Face ID or your fingerprint to see your clinic data.
          </p>
          {unlockError && <p className="text-white text-sm mb-6 max-w-[300px]">{unlockError}</p>}
          <button
            onClick={unlock}
            className="w-full max-w-[300px] h-12 rounded-lg bg-white text-primary font-semibold text-sm mb-3"
          >
            Unlock
          </button>
          <button
            onClick={async () => {
              // Local sign-out always clears the saved session, even offline, so the
              // lock can't be skipped by signing out without a connection
              await supabase.auth.signOut({ scope: 'local' });
              setLocked(false);
              navigate('/login');
            }}
            className="w-full max-w-[300px] h-12 rounded-lg text-white font-semibold text-sm border border-white/60"
          >
            Sign in with password
          </button>
        </FullScreen>
      )}
      {inBackground && !locked && (
        // Covers clinic data in the app switcher preview
        <FullScreen>
          <img src="/cscore-logo.png" alt="CScore" style={{ height: '48px', filter: 'brightness(0) invert(1)' }} />
        </FullScreen>
      )}
      {!locked && !inBackground && !online && (
        <FullScreen>
          <WifiSlash size={56} weight="light" className="text-white mb-6" />
          <h1 className="text-white text-2xl font-semibold mb-2">No connection</h1>
          <p className="text-white/80 text-sm max-w-[280px]">
            CScore needs the internet to load your clinic data. This screen goes away once you're back online.
          </p>
        </FullScreen>
      )}
    </>
  );
}

function FullScreen({ children }) {
  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center text-center px-6"
      style={{ background: 'linear-gradient(to bottom, #6C18C9, #9653E8, #CB9EF6)' }}
    >
      {children}
    </div>
  );
}
